/* ==========================================================
   Arma tu pack — custom element
   Cada instancia es independiente: puedes tener varios bloques
   en la misma página sin que se pisen.
   ========================================================== */

if (!customElements.get('pack-builder')) {
  class PackBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.inputs = Array.from(this.querySelectorAll('input[data-variant-id]'));
      this.addBtn = this.querySelector('[data-add]');
      this.label = this.querySelector('[data-label]');
      this.checkoutBtn = this.querySelector('[data-checkout]');
      this.checkoutLabel = this.querySelector('[data-checkout-label]');
      this.totalEl = this.querySelector('[data-total]');
      this.countEl = this.querySelector('[data-count]');
      this.errorEl = this.querySelector('[data-error]');
      this.savingsEl = this.querySelector('[data-savings]');

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('input', event => {
        const input = event.target;
        if (!input.matches('input[data-variant-id]')) return;
        if (this.dataset.mode === 'bundle' && input.hasAttribute('data-main') && Number(input.value) > 0) {
          this.querySelectorAll('[data-main]').forEach(el => { if (el !== input) el.value = 0; });
          input.closest('[data-product-row]').querySelector('[data-main-choice]').checked = true;
        }
        this.refresh();
      });
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.dataset.mode === 'bundle') {
        const first = this.querySelector('[data-main-choice]');
        if (first && !this.querySelector('[data-main-choice]:checked')) {
          first.checked = true;
          first.closest('[data-product-row]').querySelector('[data-main]').value = 1;
        }
      }
      this.refresh();
    }

    normalize(input) {
      const max = input.max === '' ? 999 : Math.max(0, Number(input.max) || 0);
      const value = Math.max(0, Math.min(max, Math.floor(Number(input.value) || 0)));
      input.value = value;
      return value;
    }

    onChange(event) {
      const target = event.target;
      const row = target.closest('[data-product-row]');
      if (!row) return;
      const input = row.querySelector('input[data-variant-id]');
      if (target.matches('[data-addon-check]')) input.value = target.checked ? 1 : 0;
      if (target.matches('[data-main-choice]')) {
        this.querySelectorAll('[data-main]').forEach(el => { el.value = el === input ? 1 : 0; });
      }
      if (target.matches('[data-product-variant]')) {
        const option = target.selectedOptions[0];
        input.dataset.variantId = option.value;
        ['price', 'compare', 'title'].forEach(key => { input.dataset[key] = option.dataset[key]; });
        input.max = option.dataset.max;
        row.querySelector('[data-row-price]').innerHTML = this.formatMoney(Number(option.dataset.price));
      }
      this.refresh();
    }

    onKeydown(event) {
      const tab = event.target.closest('[data-tab]');
      if (!tab || !['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const tabs = Array.from(this.querySelectorAll('[data-tab]'));
      let index = tabs.indexOf(tab);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      this.switchTab(tabs[index].dataset.tab);
      tabs[index].focus();
    }

    onClick(event) {
      if (this.busy) return;
      const step = event.target.closest('[data-step]');
      if (step) {
        const input = step.parentElement.querySelector('input');
        const next = (parseInt(input.value, 10) || 0) + parseInt(step.dataset.step, 10);
        input.value = Math.max(0, next);
        const row = input.closest('[data-product-row]');
        if (row && input.hasAttribute('data-main') && Number(input.value) > 0) {
          this.querySelectorAll('[data-main]').forEach(el => { if (el !== input) el.value = 0; });
          row.querySelector('[data-main-choice]').checked = true;
        }
        this.refresh();
        return;
      }

      const tab = event.target.closest('[data-tab]');
      if (tab) {
        this.switchTab(tab.dataset.tab);
        return;
      }

      if (event.target.closest('[data-add]')) {
        this.addToCart();
        return;
      }
      if (event.target.closest('[data-checkout]')) this.addToCart({ checkout: true });
    }

    switchTab(id) {
      let mediaId = null;

      this.querySelectorAll('[data-tab]').forEach((tab) => {
        const active = tab.dataset.tab === id;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (active) mediaId = tab.dataset.mediaId;
      });

      this.querySelectorAll('[data-panel]').forEach((panel) => {
        panel.classList.toggle('pack--hidden', panel.dataset.panel !== id);
      });

      if (this.dataset.syncGallery === 'true') this.syncGallery(mediaId);
    }

    // Mueve la galería principal a la foto del formato elegido.
    // Envuelto en try/catch: si el tema cambia su API, el selector
    // sigue funcionando aunque la galería no se mueva.
    syncGallery(mediaId) {
      if (!mediaId) return;

      const gallery = (this.closest('product-info') || document).querySelector('media-gallery');
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

      const host = gallery.closest('[data-section]') || gallery.closest('product-info');
      const sectionId = gallery.dataset.section || (host && (host.dataset.section || host.dataset.sectionId));
      if (!sectionId) return;

      try {
        gallery.setActiveMedia(sectionId + '-' + mediaId, true);
      } catch (error) {
        /* la galería de este tema usa otra API; no es crítico */
      }
    }

    getItems() {
      return this.inputs
        .map((input) => {
          const price = Number(input.dataset.price);
          return {
            id: Number(input.dataset.variantId),
            quantity: this.normalize(input),
            title: input.dataset.title || input.getAttribute('aria-label'),
            price: price,
            compare: Math.max(Number(input.dataset.compare) || 0, price)
          };
        })
        .filter((item) => item.quantity > 0);
    }

    refresh() {
      const items = this.getItems();
      const units = items.reduce((sum, item) => sum + item.quantity, 0);
      const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const compare = items.reduce((sum, item) => sum + item.compare * item.quantity, 0);
      const ahorro = compare - total;

      const rendered = this.formatMoney(total);
      if (rendered !== this.lastTotal) {
        this.totalEl.innerHTML = rendered;
        this.bump();
        this.lastTotal = rendered;
      }
      this.totalEl.classList.toggle('pack__total--zero', units === 0);

      if (this.savingsEl) {
        this.savingsEl.innerHTML = ahorro > 0 ? 'Ahorras ' + this.formatMoney(ahorro) : '';
        this.savingsEl.hidden = ahorro <= 0;
      }

      this.countEl.textContent =
        units === 0 ? 'Sin unidades' : units + (units === 1 ? ' unidad' : ' unidades');

      this.setButtonState(this.addBtn, this.label, units === 0);
      this.setButtonState(this.checkoutBtn, this.checkoutLabel, units === 0);

      this.markSelection();
      const detail = this.querySelector('[data-selection]');
      const list = this.querySelector('[data-selection-list]');
      if (detail && list) {
        detail.hidden = items.length === 0;
        list.replaceChildren();
        items.forEach(item => {
          const line = document.createElement('li');
          const name = document.createElement('span');
          const price = document.createElement('strong');
          name.textContent = item.quantity + ' × ' + item.title;
          price.innerHTML = this.formatMoney(item.price * item.quantity);
          line.append(name, price);
          list.appendChild(line);
        });
      }
      this.querySelectorAll('[data-product-row]').forEach(row => {
        const input = row.querySelector('input[data-variant-id]');
        const checkbox = row.querySelector('[data-addon-check]');
        if (checkbox) checkbox.checked = Number(input.value) > 0;
      });
      this.querySelectorAll('[data-step]').forEach(button => {
        const input = button.parentElement.querySelector('input');
        button.disabled = this.busy || (Number(button.dataset.step) < 0 ? Number(input.value) <= 0 : Number(input.value) >= Number(input.max || 999));
      });
    }

    // Sin selección el botón NO se deshabilita: apagado se leía como
    // "agotado". Queda activo y el texto explica qué falta.
    setButtonState(button, labelEl, empty) {
      if (!button || !labelEl) return;
      button.disabled = !!this.busy;
      if (this.busy) return;
      button.setAttribute('aria-disabled', empty ? 'true' : 'false');
      labelEl.textContent = empty
        ? this.dataset.emptyLabel || 'Elige tus productos'
        : button.dataset.fullLabel || labelEl.textContent;
    }

    notice(text, hint) {
      if (!this.errorEl) return;
      this.errorEl.textContent = text;
      this.errorEl.classList.toggle('pack__error--hint', !!hint);
      this.errorEl.hidden = false;
      clearTimeout(this.noticeTimer);
      if (hint) {
        this.noticeTimer = setTimeout(() => {
          this.errorEl.hidden = true;
        }, 4000);
      }
    }

    // Resalta la fila elegida y pone el contador en la pestaña, para que
    // al saltar entre formatos no se pierda de vista lo ya seleccionado.
    markSelection() {
      this.inputs.forEach((input) => {
        const row = input.closest('.pack__row');
        if (row) row.classList.toggle('is-selected', (parseInt(input.value, 10) || 0) > 0);
      });

      this.querySelectorAll('[data-panel]').forEach((panel) => {
        const units = Array.from(panel.querySelectorAll('input[data-variant-id]')).reduce(
          (sum, input) => sum + (parseInt(input.value, 10) || 0),
          0
        );
        const tab = this.querySelector('[data-tab="' + panel.dataset.panel + '"]');
        if (!tab) return;

        const badge = tab.querySelector('[data-tab-badge]');
        if (badge) {
          badge.textContent = units;
          badge.hidden = units === 0;
        }
      });
    }

    bump() {
      if (!this.totalEl) return;
      this.totalEl.classList.remove('is-bumped');
      void this.totalEl.offsetWidth; // reinicia la animación
      this.totalEl.classList.add('is-bumped');
      clearTimeout(this.bumpTimer);
      this.bumpTimer = setTimeout(() => this.totalEl.classList.remove('is-bumped'), 180);
    }

    // Replica el filtro `money` de Liquid para que el total use exactamente
    // el mismo formato que los precios de cada fila. Con Intl salía
    // "20.392 CLP" arriba y "$20.392" abajo: se ve como un error.
    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      const value = Math.abs(cents) / 100;

      const build = (decimals, thousands, decimal) => {
        const parts = value.toFixed(decimals).split('.');
        const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return decimals > 0 ? whole + decimal + parts[1] : whole;
      };

      const rules = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
        amount_no_decimals_with_space_separator: [0, ' ', '.'],
        amount_with_space_separator: [2, ' ', ','],
        amount_with_period_and_space_separator: [2, ' ', '.']
      };

      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, name) =>
        build.apply(null, rules[name] || rules.amount)
      );
    }

    async addToCart(options) {
      if (this.busy) return;
      const goToCheckout = !!(options && options.checkout);
      const grouped = new Map();
      this.getItems().forEach(item => grouped.set(item.id, (grouped.get(item.id) || 0) + item.quantity));
      const items = Array.from(grouped, ([id, quantity]) => ({ id, quantity }));
      if (this.querySelector('[data-main]') && !this.inputs.some(input => input.hasAttribute('data-main') && Number(input.value) > 0)) {
        this.notice('Elige tu producto o formato antes de añadir complementos.', true);
        return;
      }

      if (!items.length) {
        this.notice('Elige al menos un producto para continuar.', true);
        const input = this.querySelector('[data-panel]:not(.pack--hidden) input[data-variant-id]');
        if (input) input.focus();
        return;
      }

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const drawer = document.querySelector('cart-drawer');

      const sections =
        drawer && typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map((section) => section.id)
          : ['cart-icon-bubble'];

      const button = goToCheckout ? this.checkoutBtn : this.addBtn;
      const labelEl = goToCheckout ? this.checkoutLabel : this.label;

      this.errorEl.hidden = true;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('button, input, select').forEach(control => {
        control.dataset.packDisabled = control.disabled ? 'true' : 'false';
        control.disabled = true;
      });
      button.dataset.loading = 'true';
      labelEl.textContent = goToCheckout ? 'Yendo al pago…' : 'Agregando…';

      try {
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: items,
            sections: sections,
            sections_url: window.location.pathname
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(this.friendlyError(data));
        }

        if (goToCheckout) {
          window.location.href = root + 'checkout';
          return;
        }

        this.inputs.forEach((input) => {
          input.value = 0;
        });

        try {
          this.showCart(root, data, drawer);
        } catch (renderError) {
          // The items were added successfully. Do not encourage a duplicate retry.
          this.confirm(root);
        }
      } catch (error) {
        this.notice(error.message || 'No pudimos conectar con el carrito. Inténtalo de nuevo.');
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        this.querySelectorAll('[data-pack-disabled]').forEach(control => {
          control.disabled = control.dataset.packDisabled === 'true';
          delete control.dataset.packDisabled;
        });
        delete button.dataset.loading;
        this.refresh(); // repone los textos de ambos botones
      }
    }

    showCart(root, data, drawer) {
      if (!drawer) {
        // Carrito tipo página o notificación: no sacamos a la persona del producto.
        this.renderSection(data, 'cart-icon-bubble');
        this.confirm(root);
        return;
      }

      if (typeof drawer.renderContents === 'function') {
        drawer.renderContents(data);
      }

      // Dawn imprime <cart-drawer class="drawer is-empty"> cuando la página se
      // cargó con el carrito vacío, y renderContents solo reemplaza el interior.
      // Sin esta limpieza el cajón abre en blanco aunque los productos ya estén.
      drawer.classList.remove('is-empty');
      drawer.querySelectorAll('.drawer__inner.is-empty').forEach((el) => {
        el.classList.remove('is-empty');
      });

      this.renderSection(data, 'cart-icon-bubble');

      if (typeof drawer.open === 'function') drawer.open();

      // Avisamos al resto del tema: barras de envío gratis, contadores, apps.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pack-builder', cartData: data });
      }
    }

    // Shopify rechaza el lote completo si una sola variante no alcanza stock.
    friendlyError(data) {
      const left = typeof data.items_left === 'number' ? data.items_left : null;
      if (left !== null) {
        const name = data.title || 'ese formato';
        return left === 0
          ? 'Se agotó ' + name + ' mientras armabas el pack.'
          : 'Solo quedan ' + left + ' unidades de ' + name + '. Ajusta la cantidad e inténtalo de nuevo.';
      }
      return data.description || data.message || 'No se pudo agregar al carrito.';
    }

    renderSection(data, id) {
      const html = data.sections && data.sections[id];
      const target = document.getElementById(id);
      if (!html || !target) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('.shopify-section') || parsed.body;
      target.innerHTML = source.innerHTML;
    }

    confirm(root) {
      let note = this.querySelector('[data-confirm]');
      if (!note) {
        note = document.createElement('p');
        note.className = 'pack__confirm';
        note.setAttribute('data-confirm', '');
        this.querySelector('.pack__footer').appendChild(note);
      }
      note.innerHTML = 'Agregado al carrito. <a href="' + root + 'cart">Ver carrito</a>';
      note.hidden = false;

      clearTimeout(this.confirmTimer);
      this.confirmTimer = setTimeout(function () {
        note.hidden = true;
      }, 6000);
    }
  }

  customElements.define('pack-builder', PackBuilder);
}